import axios from 'axios';

const portalAxios = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || 'http://localhost:3000'
});

portalAxios.interceptors.request.use((request) => {
  const token = localStorage.getItem('partnerToken');
  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }
  return request;
});

export interface PartnerProfile {
  activeProjectCount: number;
  addressLine1?: string;
  city?: string;
  companyName: string;
  contactEmail: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactPhone?: string;
  country?: string;
  description?: string;
  id: string;
  skills?: any[];
  state?: string;
  status: string;
  zipCode?: string;
}

export interface PortalDocumentRequirement {
  description?: string;
  documentType: string;
  id: string;
  isRequired: boolean;
  name: string;
}

export interface PortalDocument {
  createdAt: string;
  documentType: string;
  expiresAt?: string;
  fileName: string;
  fileUrl: string;
  id: string;
  name: string;
  rejectionReason?: string;
  requirement?: PortalDocumentRequirement;
  reviewedAt?: string;
  status: string;
}

export interface PortalCompliance {
  compliant: boolean;
  missing: PortalDocumentRequirement[];
  pending: PortalDocument[];
  rejected: PortalDocument[];
}

export interface PortalSkill {
  id: string;
  name: string;
}

export interface PortalProject {
  clientBusinessName?: string;
  clientFirstName?: string;
  clientLastName?: string;
  createdAt: string;
  endDate?: string;
  id: string;
  leadName?: string;
  location?: string;
  services?: { serviceName: string; tasks: string }[];
  startDate?: string;
  status: string;
}

const getProfile = async (): Promise<PartnerProfile> => {
  const response = await portalAxios.get('/partner-portal/profile');
  return response.data;
};

const updateProfile = async (
  data: Partial<PartnerProfile>
): Promise<PartnerProfile> => {
  const response = await portalAxios.patch('/partner-portal/profile', data);
  return response.data;
};

const getRequirements = async (): Promise<PortalDocumentRequirement[]> => {
  const response = await portalAxios.get(
    '/partner-portal/document-requirements'
  );
  return response.data;
};

const getDocuments = async (): Promise<PortalDocument[]> => {
  const response = await portalAxios.get('/partner-portal/documents');
  return response.data;
};

const uploadDocument = async (data: {
  documentType: string;
  expiresAt?: string;
  file: File;
  name: string;
  requirementId?: string;
}): Promise<PortalDocument> => {
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('name', data.name);
  formData.append('documentType', data.documentType);
  if (data.requirementId) {
    formData.append('requirementId', data.requirementId);
  }
  if (data.expiresAt) {
    formData.append('expiresAt', data.expiresAt);
  }
  const response = await portalAxios.post(
    '/partner-portal/documents/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

const deleteDocument = async (id: string): Promise<void> => {
  await portalAxios.delete(`/partner-portal/documents/${id}`);
};

const getCompliance = async (): Promise<PortalCompliance> => {
  const response = await portalAxios.get('/partner-portal/compliance');
  return response.data;
};

const getAvailableSkills = async (): Promise<PortalSkill[]> => {
  const response = await portalAxios.get('/partner-portal/available-skills');
  return response.data;
};

const getProjects = async (): Promise<PortalProject[]> => {
  const response = await portalAxios.get('/partner-portal/projects');
  return response.data;
};

const getProjectDetail = async (projectId: string): Promise<any> => {
  const response = await portalAxios.get(
    `/partner-portal/projects/${projectId}`
  );
  return response.data;
};

const getPartnerSkills = async (): Promise<PortalSkill[]> => {
  const response = await portalAxios.get('/partner-portal/skills');
  return response.data;
};

const updateSkills = async (skillIds: string[]): Promise<PortalSkill[]> => {
  const response = await portalAxios.patch('/partner-portal/skills', {
    skillIds
  });
  return response.data;
};

export default {
  deleteDocument,
  getAvailableSkills,
  getCompliance,
  getDocuments,
  getPartnerSkills,
  getProfile,
  getProjectDetail,
  getProjects,
  getRequirements,
  updateProfile,
  updateSkills,
  uploadDocument
};
